import { MotionConfig } from 'motion/react';
import { useSyncExternalStore, type ReactNode } from 'react';

/**
 * Makes motion/react honour reduced motion.
 *
 * The CSS in globals.css already tames CSS transitions, but framer-motion
 * animates in JS, so it needs telling separately. We treat motion as reduced
 * when EITHER the OS `prefers-reduced-motion` is set OR the in-app toggle is on
 * (applyAppearance stamps `data-reduced-motion` on <html>).
 */
const QUERY = '(prefers-reduced-motion: reduce)';

function subscribe(onChange: () => void): () => void {
  const mq = window.matchMedia(QUERY);
  mq.addEventListener('change', onChange);
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-reduced-motion'],
  });
  return () => {
    mq.removeEventListener('change', onChange);
    observer.disconnect();
  };
}

function prefersReduced(): boolean {
  return (
    document.documentElement.getAttribute('data-reduced-motion') === 'true' ||
    window.matchMedia(QUERY).matches
  );
}

export function MotionProvider({ children }: { children: ReactNode }) {
  const reduced = useSyncExternalStore(subscribe, prefersReduced, () => false);
  return <MotionConfig reducedMotion={reduced ? 'always' : 'never'}>{children}</MotionConfig>;
}
